import {
	deepEquals,
	ensureErrorType,
	isNullOrEmpty,
	isPromiseAlike, Paths,
	readNestedProperty,
	writeNestedProperty
} from "@2d-front-monorepo/core";
import {MvvmValidation, MvvmValidationResult} from "./MvvmControlProps";
import {createTrackingProxy, SyntheticChannel} from "./MvvmTrackingProxy";

const ON_CHANGE_ERROR: string = "onChangeError";
const SUBMIT_ERROR: string = "submitError";
const GLOBAL_VALIDATION_ERROR: string = "globalValidationError";
const FIELD_VALIDATION_ERROR: string = "fieldValidationError";

function shouldSkipValidation(validator: ValidatorEntry): boolean {
	const {validation} = validator;
	return validation.required == null && validation.validate == null;
}

function isRequiredValueMissing(value: any): boolean {
	if (value == null) {
		return true;
	}

	if (typeof value === "string") {
		return isNullOrEmpty(value);
	}

	if (Array.isArray(value)) {
		return value.length === 0;
	}

	if (value instanceof Date) {
		return isNaN(value.getTime());
	}

	if (typeof value === "object") {
		const proto = Object.getPrototypeOf(value);
		if (proto === Object.prototype || proto === null) {
			return Object.keys(value).length === 0;
		}

		// Non-plain objects expose no enumerable own keys reliably (getters, #private
		// fields). Collections (Map/Set, FileList, NodeList) are empty by length/size;
		// any other instance (DateOnly, DateTime, ...) counts as present.
		if (typeof (value as { length?: unknown }).length === "number") {
			return (value as { length: number }).length === 0;
		}
		if (typeof (value as { size?: unknown }).size === "number") {
			return (value as { size: number }).size === 0;
		}
		return false;
	}

	return isNaN(value);
}

function isObjectLike(value: unknown): boolean {
	return value !== null && typeof value === "object";
}

// Sync validators stay sync: awaiting a plain value would still cost a microtask.
async function awaitMaybe<TResult>(value: TResult | Promise<TResult>): Promise<TResult> {
	return isPromiseAlike<TResult>(value) ? await value : value;
}

export interface ErrorEntry {
	error: string;
	controlId: string;
	value: any;
	type: string;
}

export type MvvmUpdater<T extends object> = (action: T) => void;

export type MvvmContextInitStatus = "loading" | "ready" | "error";

export type MvvmContextUnsubscribe = () => void;

export interface IMvvmBaseContextState {
	getExceptions(): Error[];

	hasExceptions(): boolean;

	getErrors(): ErrorEntry[];

	hasErrors(): boolean;

	isInvalid(): boolean;

	isSubmitting(): boolean;

	isValidating(): boolean;

	triggerValidation(): void;

	/*
	 * Validates the context and returns a promise that resolves when validation is complete.
	 * @returns true if validation passed, false if validation failed.
	 */
	validate(): Promise<boolean>;

	setException(name: string, error?: Error): void;

	get initStatus(): MvvmContextInitStatus;

	get initError(): Error | undefined;
}

export interface IMvvmContextState<T extends object> extends IMvvmBaseContextState {
	clearErrors(): void;

	getError(path: Paths<T>): ErrorEntry | undefined;

	getValue<TValue>(path: Paths<T>): TValue | undefined;

	/*
	 * Writes `value` at `path` (no-op when deep-equal to the current value). By default the
	 * write notifies subscribers and schedules validation/onChange; `{validate: false}`
	 * still notifies subscribers (a host preview of the field sees every keystroke) but
	 * leaves validation and onChange to a later write — text controls use it per keystroke
	 * and validate on blur.
	 */
	setValue(path: Paths<T>, value: any, options?: { validate?: boolean }): void;

	get state(): T;

	update(updater: MvvmUpdater<T>): void;

	validationEnds(): Promise<void>;
}

export interface MvvmContextValidationResult<T> {
	error: string | undefined;
	path: Paths<T>;
	model?: object;
	type?: string;
}

export type MvvmContextValidationSync<T extends object> = (model: T) => MvvmContextValidationResult<T>[];
export type MvvmContextValidationAsync<T extends object> = (model: T) => Promise<MvvmContextValidationResult<T>[]>;
export type MvvmContextValidationMethod<T extends object> =
	MvvmContextValidationSync<T>
	| MvvmContextValidationAsync<T>;

export type MvvmContextChangeSync<T extends object> = (model: T) => void;
export type MvvmContextChangeAsync<T extends object> = (model: T) => Promise<void>;

export type MvvmContextChangeMethod<T extends object> =
	MvvmContextChangeSync<T>
	| MvvmContextChangeAsync<T>;

interface ValidatorEntry {
	controlId: string;
	validation: MvvmValidation<any>;
}

export type MvvmContextInitialState<T extends object> = T | (() => T);

function resolveInitialState<T extends object>(initialState: MvvmContextInitialState<T>): T {
	return typeof initialState === "function" ? (initialState as () => T)() : initialState;
}

const _aggregateChannels: readonly string[] = [
	SyntheticChannel.validating,
	SyntheticChannel.submitting,
	SyntheticChannel.errors,
	SyntheticChannel.exceptions
];

export class MvvmContextState<T extends object> implements IMvvmContextState<T> {
	private static readonly _resolved = Promise.resolve();


	private _disposed: boolean;
	private readonly _errors: Map<string, ErrorEntry>;
	private _errorsArrayCache: ErrorEntry[] | undefined;
	private readonly _exceptions: Map<string, Error>;
	private _globalValidation: MvvmContextValidationMethod<T> | undefined;
	private _submitValidation: MvvmContextValidationMethod<T> | undefined;
	private readonly _innerContexts: Set<MvvmContextState<any>>;
	private _isSubmitting: boolean;
	private _isValidating: boolean;
	private _onChange: MvvmContextChangeMethod<T> | undefined;
	private _onChangeInFlight: Promise<void> | undefined;
	// Paths written while the onChange handler is in flight (undefined when it is not).
	private _onChangeInFlightWrites: Set<string> | undefined;
	// Paths ever written from outside an onChange run (user edits, host effects). A write to
	// one of these while the handler is in flight is a change the handler never saw.
	private readonly _externalPaths: Set<string>;
	private _onChangeQueued: boolean;
	private _parentContext: MvvmContextState<any> | undefined;
	private readonly _queuedValidations: Set<Paths<any>>;
	private _state: T;
	private _validationEnds: Promise<void> | undefined;
	private readonly _validationEndsQueue: Set<Paths<any>>;
	private _pendingFireOnChange: boolean;
	private _pendingRunSubmit: boolean;
	private readonly _validators: Map<string, ValidatorEntry>;
	private readonly _subscribers: Map<string, Set<() => void>>;
	private _initStatus: MvvmContextInitStatus;
	private _initError: Error | undefined;
	private _initStarted: boolean;
	// Visibility gates for surfacing child-context problems through this context's
	// getErrors/hasErrors/getExceptions (the parent's error collector). Set when a submit
	// finds problems in child contexts; NOT consulted by the blocking checks
	// (isInvalid/hasExceptions), which always recurse. Lifetimes mirror the previous
	// copy-merge behavior: errors hide again at the end of the next own validation pass,
	// exceptions at the start of the next submit.
	private _showChildErrors: boolean;
	private _showChildExceptions: boolean;

	constructor(state: MvvmContextInitialState<T>) {
		this._isSubmitting = this._isValidating = false;
		this._globalValidation = undefined;
		this._disposed = false;
		this._parentContext = undefined;
		this._state = resolveInitialState(state);
		this._errors = new Map();
		this._errorsArrayCache = undefined;
		this._validators = new Map();
		this._exceptions = new Map();
		this._queuedValidations = new Set();
		this._innerContexts = new Set();
		this._validationEndsQueue = new Set();
		this._subscribers = new Map();
		this._initStatus = "ready";
		this._initError = undefined;
		this._initStarted = false;
		this._onChangeInFlightWrites = undefined;
		this._externalPaths = new Set();
		this._onChangeQueued = false;
		this._pendingFireOnChange = false;
		this._pendingRunSubmit = false;
		this._showChildErrors = false;
		this._showChildExceptions = false;
	}

	get initStatus(): MvvmContextInitStatus {
		return this._initStatus;
	}

	get initError(): Error | undefined {
		return this._initError;
	}

	// Run a one-shot async initializer that owns the "loading" → "ready"/"error" transition.
	// Failure is terminal for this instance: the run-once guard and absence of any reset path
	// mean recovery requires recreating the context (deps change or remount).
	runInitialization(initialize: (ctx: IMvvmContextState<T>) => Promise<void>): void {
		if (this._initStarted || this._disposed) return;
		this._initStarted = true;
		this._initStatus = "loading";
		this._notify(SyntheticChannel.initStatus);

		initialize(this).then(
			() => {
				if (this._disposed) return;
				this._initStatus = "ready";
				this._notify(SyntheticChannel.initStatus);
			},
			e => {
				if (this._disposed) return;
				this._initStatus = "error";
				this._initError = ensureErrorType(e);
				this._notify(SyntheticChannel.initStatus);
			}
		);
	}

	getErrors(): ErrorEntry[] {
		// Cached so repeated render-time access keeps stable reference identity. The cache
		// is invalidated by every mutation that touches `_errors`, by visibility flips, and
		// (while child errors are visible) by child $errors notifications bubbling up.
		if (this._errorsArrayCache != null) {
			return this._errorsArrayCache;
		}

		const errors = Array.from(this._errors.values());
		if (this._showChildErrors) {
			for (const context of this._innerContexts) {
				errors.push(...context.getErrors());
			}
		}

		return this._errorsArrayCache = errors;
	}

	hasErrors(): boolean {
		if (this._errors.size > 0) return true;
		if (!this._showChildErrors) return false;
		for (const context of this._innerContexts) {
			if (context.hasErrors()) return true;
		}
		return false;
	}

	clearErrors = (): void => {
		// Surfaced child errors clear too — under the old copy-merge they lived in this
		// map and went with it. Child exceptions keep their submit-scoped lifetime.
		this._setChildProblemVisibility(false, this._showChildExceptions);
		if (this._errors.size === 0) return;
		const paths = Array.from(this._errors.keys());
		this._errors.clear();
		this._errorsArrayCache = undefined;
		if (this._disposed) return;
		for (const p of paths) {
			this._notify(SyntheticChannel.errorForPath(p));
		}
		this._notify(SyntheticChannel.errors);
	};

	getError = (path: Paths<T>): ErrorEntry | undefined => this._errors.get(path);

	getValue = <TValue>(path: Paths<T>): TValue | undefined => readNestedProperty(this._state, path);

	setValue = (path: Paths<T>, value: any, options?: { validate?: boolean }): void => {
		// A late async handler (debounced input, resolved fetch) can land after dispose;
		// _state is gone by then and writeNestedProperty would throw on the undefined model.
		if (this._disposed) return;

		const oldValue = readNestedProperty(this._state, path);
		if (oldValue == null && value == null) {
			return;
		}

		if (deepEquals(oldValue, value)) {
			return;
		}

		writeNestedProperty(this._state, path, value);

		if (options?.validate === false) {
			this._notify(path, isObjectLike(value));
			return;
		}

		this.enqueueValidation(path);
	};

	get state(): T {
		return this._state;
	}

	update = (updater: (action: T) => void): void => {
		// Thin convenience over the unified write path: mutate without holding a model
		// reference. Each per-path write flows through enqueueValidation (notify + microtask-
		// batched validation/onChange), exactly like writes through the tracking facade.
		// A fresh WeakMap scopes proxy identity to this single update — nested proxies are
		// not cached across calls.
		if (this._disposed) return;
		const proxy = createTrackingProxy(this._state, "", {
			onWrite: path => this.enqueueValidation(path as Paths<T>)
		}, new WeakMap()) as T;
		updater(proxy);
	};

	async validate(): Promise<boolean> {
		this.triggerValidation();
		await this.validationEnds();
		return !this.isInvalid();
	}

	async validationEnds(): Promise<void> {
		const own = this._validationEnds ?? MvvmContextState._resolved;
		if (this._innerContexts.size === 0) {
			return own;
		}

		// Include child contexts: triggerValidation/validate kick off their passes too, and
		// validity checks (isInvalid) recurse into them — resolving before they settle would
		// report validity against stale child state.
		const all = [own, ...Array.from(this._innerContexts, context => context.validationEnds())];
		await Promise.all(all);
	}

	dispose = (): void => {
		if (this._disposed) {
			return;
		}

		this._disposed = true;
		this.clearErrors();
		this._exceptions.clear();
		this._validators.clear();
		this._queuedValidations.clear();
		this._validationEndsQueue.clear();
		this._pendingFireOnChange = false;
		this._pendingRunSubmit = false;
		this._showChildErrors = false;
		this._showChildExceptions = false;
		this._subscribers.clear();
		this._state = undefined!;
		this._onChange = undefined;
		this._onChangeInFlight = undefined;
		this._onChangeInFlightWrites = undefined;
		this._externalPaths.clear();
		this._validationEnds = undefined;
		this._globalValidation = undefined;
		this._submitValidation = undefined;
		this._initError = undefined;

		if (this._parentContext != null) {
			this._parentContext.unregisterChildren(this);
			this._parentContext = undefined;
		}
	};

	enqueueValidation = <TModel extends object>(path: Paths<TModel>): void => {
		if (this._disposed) return;

		// Every notifying write funnels through here, which is what lets _callOnChange tell
		// the handler's own write-backs from changes made behind its back: a path written
		// while no handler runs is external by definition (initializer writes are neither).
		if (this._onChangeInFlightWrites != null) {
			this._onChangeInFlightWrites.add(path);
		} else if (this._initStatus !== "loading") {
			this._externalPaths.add(path);
		}

		// Notify the field path immediately so the UI reflects the value change even when
		// validation runs asynchronously. Subtree-down notification fires when the new value
		// is itself an object/array, so subscribers reading nested leaves re-render too.
		const value = readNestedProperty(this._state, path as Paths<T>);
		this._notify(path, isObjectLike(value));

		if (this._validators.size === 0) {
			if (this._globalValidation == null) {
				// No validators — batch onChange per microtask so a multi-write update()
				// or facade burst fires it once. (With validators, _runValidations owns
				// onChange firing so we don't double-call.)
				this._queueOnChange();
				return;
			}
			// No field validators, but a global validation exists: fall through and schedule
			// a pass so its errors stay in sync with changes instead of going stale until submit.
		}

		const wasEmpty = this._queuedValidations.size === 0;
		this._queuedValidations.add(path);

		if (wasEmpty) {
			queueMicrotask(this._runQueuedValidations.bind(this));
		}
	};

	subscribe = (path: string, cb: () => void): MvvmContextUnsubscribe => {
		let set = this._subscribers.get(path);
		if (set == null) {
			set = new Set();
			this._subscribers.set(path, set);
		}
		set.add(cb);
		return () => {
			const s = this._subscribers.get(path);
			if (s == null) return;
			s.delete(cb);
			if (s.size === 0) this._subscribers.delete(path);
		};
	};

	getExceptions = (): Error[] => {
		const exceptions = Array.from(this._exceptions.values());
		if (this._showChildExceptions) {
			for (const context of this._innerContexts) {
				exceptions.push(...context.getExceptions());
			}
		}
		return exceptions;
	};

	hasExceptions = (): boolean => {
		if (this._exceptions.size > 0) return true;
		for (const context of this._innerContexts) {
			if (context.hasExceptions()) return true;
		}
		return false;
	};

	isInvalid = (): boolean => {
		if (this._errors.size > 0) return true;
		for (const context of this._innerContexts) {
			if (context.isInvalid()) return true;
		}
		return false;
	};

	isSubmitting = (): boolean => {
		if (this._isSubmitting) return true;
		for (const context of this._innerContexts) {
			if (context.isSubmitting()) return true;
		}
		return false;
	};

	isValidating = (): boolean => {
		if (this._isValidating) return true;
		for (const context of this._innerContexts) {
			if (context.isValidating()) return true;
		}
		return false;
	};

	triggerValidation = (): void => {
		// Validating changes nothing, so the consumer's onChange does not fire (no handler in
		// the apps relies on it; they all fetch/derive from real edits).
		this._triggerValidation(false, false);
		for (const context of this._innerContexts) {
			context._triggerValidation(false, false);
		}
	};

	handleSubmit = async (onSubmit: (data: T) => Promise<void>): Promise<void> => {
		try {
			this._isSubmitting = true;
			this._notify(SyntheticChannel.submitting);
			this.clearErrors();
			this._setChildProblemVisibility(false, false);
			if (this._exceptions.size > 0) {
				this._exceptions.clear();
				this._notify(SyntheticChannel.exceptions);
			}

			// Children first, then this context's own fields regardless — a user who fixes the
			// child errors must not discover the parent's afterwards. Submit validation is the
			// exception: it can have side effects (the permits payment popup opens from inside
			// it), so it only runs when the children are valid.
			const childrenInvalid = await this._validateInnerContexts();

			this._triggerValidation(false, !childrenInvalid);
			await this.validationEnds();

			// If the request raced an in-flight pass that was already past its submit step,
			// the pending flag survives the pass — run a fresh one that consumes it.
			while (this._pendingRunSubmit && !this._disposed) {
				this._triggerValidation(false, false);
				await this.validationEnds();
			}

			// Own pass ends hide surfaced child problems (see _runValidations' finally), so
			// surface them after it, once every pass this submit started has settled.
			this._surfaceChildProblems();

			// A submit validator that threw (vs. returning errors) lands in _exceptions, not
			// _errors — isInvalid() would miss it and we'd proceed to onSubmit anyway. The
			// credit-card flow relies on this: closing the payment popup rejects from inside
			// submitValidation, and we must NOT then create the permit.
			if (this.isInvalid() || this.hasExceptions()) {
				return;
			}

			await onSubmit(this._state);

		} catch (error) {
			this.setException(SUBMIT_ERROR, ensureErrorType(error));

		} finally {
			this._isSubmitting = false;
			this._notify(SyntheticChannel.submitting);
		}
	};

	registerChildren(context: MvvmContextState<any>): void {
		context.setParent(this);
		const sizeBefore = this._innerContexts.size;
		this._innerContexts.add(context);
		if (this._innerContexts.size !== sizeBefore) this._notifyAggregateChannels();
	}

	registerValidation(path: Paths<T>, controlId: string, validation: MvvmValidation<any> | undefined): void {
		if (validation == null) {
			// The control stays mounted but its validation prop was removed (conditional
			// validation): drop any recorded error too — with no validator left on the path,
			// nothing else could ever clear it and validate() would fail forever.
			this.unregisterValidation(path);
			return;
		}

		let existing = this._validators.get(path);
		if (existing == null) {
			existing = {controlId, validation};
			this._validators.set(path, existing);
			return;

		} else if (existing.validation === validation) {
			return;
		}

		existing.controlId = controlId;
		existing.validation = validation;
	}

	setException(name: string, error?: Error): void {
		const oldValue = this._exceptions.get(name);
		if (oldValue === error) {
			return;
		}

		if (error == null) {
			this._exceptions.delete(name);
		} else {
			this._exceptions.set(name, error);
		}

		this._notify(SyntheticChannel.exceptions);
	}

	setOnChange(onChange: MvvmContextChangeMethod<T> | undefined): void {
		this._onChange = onChange;
	}

	setParent(context: MvvmContextState<any>): void {
		this._parentContext = context;
	}

	setValidation(validation: MvvmContextValidationMethod<T> | undefined): void {
		this._globalValidation = validation;
	}

	setSubmitValidation(validation: MvvmContextValidationMethod<T> | undefined): void {
		this._submitValidation = validation;
	}

	unregisterChildren(context: MvvmContextState<any>): void {
		if (this._innerContexts.delete(context)) this._notifyAggregateChannels();
	}

	unregisterValidation(path: Paths<T>): void {
		this._unsetValidation(path);
		// A control owning this path is going away; any error it produced must go with it,
		// otherwise hasErrors()/isInvalid() report stale state for an invisible field.
		if (this._errors.delete(path)) {
			this._errorsArrayCache = undefined;
			if (!this._disposed) {
				this._notify(SyntheticChannel.errorForPath(path));
				this._notify(SyntheticChannel.errors);
			}
		}
	}

	// Fire every aggregate channel locally (which bubbles up via _notify). Used when the set of
	// child contexts changes, since that can alter any aggregate read without a path-level change.
	private _notifyAggregateChannels(): void {
		if (this._disposed) return;
		for (const channel of _aggregateChannels) {
			this._notify(channel);
		}
	}

	// Fires every subscriber whose path is the changed path, a parent of it, or — when the
	// new value is an object/array — a descendant of it. Subscribers are deduped so a single
	// component that subscribes to multiple matching paths still re-renders once.
	private _notify(path: string, subtree: boolean = false): void {
		if (this._disposed) return;

		// While child errors are surfaced, the aggregate errors array includes entries owned
		// by children — a child's $errors notification (bubbled up below the child) must
		// invalidate the cached aggregate. Own mutations invalidate before notifying already.
		if (path === SyntheticChannel.errors && this._showChildErrors) {
			this._errorsArrayCache = undefined;
		}

		// Aggregate synthetic channels reflect combined self+children state. A child flipping
		// one of them (e.g. a nested context finishing its validation pass) must re-render
		// consumers reading the aggregate on a parent, which only subscribe to the parent's
		// channel — so bubble up the parent chain before the local-subscriber fast path.
		if (this._parentContext != null && _aggregateChannels.includes(path)) {
			this._parentContext._notify(path);
		}

		if (this._subscribers.size === 0) return;

		const seen = new Set<() => void>();

		const exact = this._subscribers.get(path);
		if (exact != null) for (const cb of exact) {
			seen.add(cb);
		}

		// Synthetic channels ($errors, $err:a.b, ...) are flat names, not state paths —
		// walking them would notify "$err:a" subscribers about "$err:a.b" changes.
		if (path.startsWith("$")) {
			for (const cb of seen) {
				cb();
			}
			return;
		}

		// A write AT the path replaced the object read there. Deliberately not fired by the
		// ancestor walk below: leaf writes don't change a parent object's identity.
		const objExact = this._subscribers.get(SyntheticChannel.objectForPath(path));
		if (objExact != null) for (const cb of objExact) {
			seen.add(cb);
		}

		// Walk up parent paths: "a.b.c" → "a.b" → "a" → ""
		let parent = path;
		while (parent !== "") {
			const dot = parent.lastIndexOf(".");
			parent = dot === -1 ? "" : parent.substring(0, dot);
			const parentSet = this._subscribers.get(parent);
			if (parentSet != null) for (const cb of parentSet) {
				seen.add(cb);
			}
		}

		// Walk down to leaves nested inside the changed path. Only needed for whole-subtree
		// replacement: a primitive write at "a.b" cannot invalidate "a.b.c" subscribers.
		if (subtree) {
			if (path === "") {
				for (const set of this._subscribers.values()) {
					for (const cb of set) {
						seen.add(cb);
					}
				}
			} else {
				const prefix = `${path}.`;
				// Replacing this subtree also replaces every object previously read below it.
				const objPrefix = SyntheticChannel.objectForPath(prefix);
				for (const [key, set] of this._subscribers) {
					if (key.startsWith(prefix) || key.startsWith(objPrefix)) {
						for (const cb of set) {
							seen.add(cb);
						}
					}
				}
			}
		}

		for (const cb of seen) {
			cb();
		}
	}

	// Coalesces onChange for the no-validator path: any number of writes in the same
	// microtask window (one update() call, a facade write burst) fire onChange once.
	private _queueOnChange(): void {
		if (this._onChangeQueued) return;
		this._onChangeQueued = true;
		queueMicrotask(() => {
			this._onChangeQueued = false;
			void this._callOnChange();
		});
	}

	private _callOnChange(): Promise<void> {
		// While initialize() is running, mutations from inside the initializer must not auto-fire
		// onChange — the consumer's onChange typically fetches downstream data and may capture
		// state that's still being populated. The initializer can call its own logic explicitly.
		if (this._disposed || this._initStatus === "loading" || !this._onChange) {
			return MvvmContextState._resolved;
		}

		// Writes made while the onChange handler itself is in flight (directly or after its
		// awaits) are part of producing the new state, not new changes — re-firing onChange
		// for them would loop forever for handlers that write back (fetch → store result).
		// They are recorded (enqueueValidation) and judged once the handler settles.
		if (this._onChangeInFlight != null) {
			return this._onChangeInFlight;
		}

		const run = async (): Promise<void> => {
			if (this._disposed || !this._onChange) return;

			try {
				const awaitable = this._onChange(this._state);
				if (isPromiseAlike(awaitable)) {
					await awaitable;
				}

				this.setException(ON_CHANGE_ERROR, undefined);

			} catch (e) {
				this.setException(ON_CHANGE_ERROR, ensureErrorType(e));
			}
		};

		const wrapped = (async (): Promise<void> => {
			const writes = this._onChangeInFlightWrites = new Set<string>();
			await run();
			if (this._disposed) return;

			// A mid-flight write to a path the user (or a host effect) writes is a change the
			// handler never saw — zone B picked while zone A's fetch was running. Re-run once
			// so it settles on the final state. The handler's own write-backs land on derived
			// paths nobody else writes, so a fetch → store handler still runs once per change.
			// ponytail: exactly one re-run, never chased — a handler that rewrites a user path
			// with a different value on every run gets one extra call, not a loop.
			for (const path of writes) {
				if (this._externalPaths.has(path)) {
					this._onChangeInFlightWrites = new Set<string>();
					await run();
					return;
				}
			}
		})().finally(() => {
			this._onChangeInFlightWrites = undefined;
			if (this._onChangeInFlight === wrapped) {
				this._onChangeInFlight = undefined;
			}
		});
		this._onChangeInFlight = wrapped;

		return wrapped;
	}

	// Flips the child-problem visibility gates, notifying the affected aggregate channels
	// so collectors re-read. The errors array cache includes child errors while visible,
	// so any errors flip invalidates it.
	private _setChildProblemVisibility(errors: boolean, exceptions: boolean): void {
		if (this._showChildErrors !== errors) {
			this._showChildErrors = errors;
			this._errorsArrayCache = undefined;
			this._notify(SyntheticChannel.errors);
		}
		if (this._showChildExceptions !== exceptions) {
			this._showChildExceptions = exceptions;
			this._notify(SyntheticChannel.exceptions);
		}
	}

	private _clearErrorsByControlId(controlId: string): void {
		if (this._errors.size === 0) return;

		const cleared: string[] = [];
		for (const [key, error] of this._errors) {
			if (error.controlId === controlId) {
				cleared.push(key);
			}
		}

		if (cleared.length === 0) return;
		for (const key of cleared) {
			this._errors.delete(key);
		}
		this._errorsArrayCache = undefined;
		for (const key of cleared) {
			this._notify(SyntheticChannel.errorForPath(key));
		}
		this._notify(SyntheticChannel.errors);
	}

	private _getValidationQueue(): Paths<any>[] {
		return Array.from(this._validators.keys());
	}

	private async _runGlobalValidation(validation: MvvmContextValidationMethod<T> | undefined, controlId: string): Promise<void> {
		if (this._disposed) return;
		this._clearErrorsByControlId(controlId);
		if (!validation) {
			return;
		}

		try {
			let validationResult: MvvmContextValidationResult<T>[];
			const validationMaybePromise = validation(this._state);
			if (isPromiseAlike(validationMaybePromise)) {
				validationResult = await validationMaybePromise;
			} else {
				validationResult = validationMaybePromise;
			}

			for (const result of validationResult) {
				this._setError(result.error, result.path, controlId, readNestedProperty(result.model ?? this._state, result.path as Paths<object>));
			}
		} catch (e) {
			this.setException(GLOBAL_VALIDATION_ERROR, ensureErrorType(e));
		}
	}

	private _runQueuedValidations(): void {
		if (this._disposed) return;

		const localValidations = Array.from(this._queuedValidations);
		this._queuedValidations.clear();

		const filtered = localValidations.filter(path => {
			const validator = this._validators.get(path);
			return validator != null && !shouldSkipValidation(validator);
		});

		// Nothing on this batch validates and no global validation could go stale: skip the
		// pass (and its two $validating flips, each a host re-render) and just deliver the
		// batched onChange. While a pass is in flight, _runValidations queues instead.
		if (filtered.length === 0 && this._globalValidation == null && !this._isValidating) {
			this._queueOnChange();
			return;
		}

		this._runValidations(filtered, true, false);
	}

	// Resolves the field's outcome, then records it with a single _setError so an error that
	// persists across runs (same message, same value) causes no notification and never
	// flickers through "no error" while an async validator is pending.
	private async _runValidation(path: Paths<any>): Promise<void> {
		if (this._disposed) return;
		const validator = this._validators.get(path);
		if (validator == null) return;

		const {controlId, validation} = validator;
		const value = readNestedProperty(this._state, path as Paths<T>);

		let error: string | MvvmValidationResult | undefined;
		let type: string | undefined;

		const required = validation.required;
		if (typeof required === "string") {
			if (isRequiredValueMissing(value)) {
				error = required;
				type = "required";
			}
		} else if (required != null) {
			error = await awaitMaybe(required(value));
			type = "required";
		}

		if (error == null && validation.validate != null) {
			error = await awaitMaybe(validation.validate(value));
			type = undefined;
		}

		this._setError(error, path, controlId, value, type);
	}

	private async _runAllValidations(entries: Paths<any>[]): Promise<void> {
		// A validator that throws (instead of returning an error) must not escape the pass:
		// keystroke passes have no awaiter, so the rejection would go unhandled and the
		// sibling validators sharing the Promise.all would be abandoned mid-flight. Let every
		// entry settle, then surface the first failure like global validation does — the
		// collector shows it and hasExceptions() blocks submit. A clean batch clears it.
		const results = await Promise.allSettled(entries.map(entry => this._runValidation(entry)));
		const failure = results.find(result => result.status === "rejected");
		this.setException(FIELD_VALIDATION_ERROR, failure == null ? undefined : ensureErrorType(failure.reason));
	}

	private _runValidations(entries: Paths<any>[], fireOnChange: boolean, runSubmitValidation: boolean): void {
		if (this._isValidating) {
			// If we're already running validations, queue the paths and validate them
			// after the current async pass settles so stale results cannot win races.
			// The flags are accumulated (not dropped) so a handleSubmit that races an
			// in-flight keystroke pass still gets its submit validation run.
			for (const entry of entries) {
				this._validationEndsQueue.add(entry);
			}
			this._pendingFireOnChange ||= fireOnChange;
			this._pendingRunSubmit ||= runSubmitValidation;

		} else {
			this._isValidating = true;
			this._notify(SyntheticChannel.validating);
			this._validationEnds = (async () => {
				try {
					if (fireOnChange) {
						await this._callOnChange();
					}

					if (entries.length > 0) {
						await this._runAllValidations(entries);
					} else if (this._validators.size === 0) {
						this.clearErrors();
					}

					// Drain field validations and onChange requests queued while we were
					// already validating, then run global/submit validation once, then drain
					// anything those queued. Pending flags are consumed wherever the pass is
					// when they arrive, so no request is lost to the in-flight race.
					let ranGlobal = false;
					let ranSubmit = false;
					while (true) {
						if (this._validationEndsQueue.size > 0) {
							const queue = Array.from(this._validationEndsQueue.values());
							this._validationEndsQueue.clear();
							await this._runAllValidations(queue);
							continue;
						}

						if (this._pendingFireOnChange) {
							this._pendingFireOnChange = false;
							await this._callOnChange();
							continue;
						}

						if (!ranGlobal) {
							ranGlobal = true;
							await this._runGlobalValidation(this._globalValidation, "global");
							continue;
						}

						if ((runSubmitValidation || this._pendingRunSubmit) && !ranSubmit) {
							ranSubmit = true;
							this._pendingRunSubmit = false;
							await this._runGlobalValidation(this._submitValidation, "submit");
							continue;
						}

						break;
					}

				} finally {
					this._validationEnds = undefined;
					this._isValidating = false;
					// Child errors stay surfaced only until this context's next validation
					// pass (same lifetime the previous copy-merge gave them). Child
					// exceptions persist until the next submit clears them.
					this._setChildProblemVisibility(false, this._showChildExceptions);
					this._notify(SyntheticChannel.validating);
				}
			})();
		}
	}

	private _setError(error: string | MvvmValidationResult | undefined, path: string, controlId: string, value: any, type: string | undefined = undefined): boolean {
		if (error == null) {
			this._unsetError(path);
			return true;
		}

		let message: string;
		let errorType: string | undefined;

		if (typeof error === "string") {
			message = error;
			errorType = type;
		} else {
			message = error.error;
			errorType = type || error.type;
		}

		if (isNullOrEmpty(message)) {
			this._unsetError(path);
			return true;
		}

		const existing = this._errors.get(path);
		const next: ErrorEntry = {error: message, controlId, value, type: errorType ?? "validation"};
		if (existing != null && existing.error === next.error && existing.type === next.type && existing.controlId === next.controlId && Object.is(existing.value, next.value)) {
			return false;
		}
		this._errors.set(path, next);
		this._errorsArrayCache = undefined;
		this._notify(SyntheticChannel.errorForPath(path));
		this._notify(SyntheticChannel.errors);
		return false;
	}

	private _triggerValidation(fireOnChange: boolean, runSubmitValidation: boolean): void {
		this._runValidations(this._getValidationQueue(), fireOnChange, runSubmitValidation);
	}

	private _unsetError(path: string): void {
		if (!this._errors.delete(path)) return;
		this._errorsArrayCache = undefined;
		this._notify(SyntheticChannel.errorForPath(path));
		this._notify(SyntheticChannel.errors);
	}

	private _unsetValidation(path: string): void {
		this._validators.delete(path);
	}

	private async _validateInnerContexts(): Promise<boolean> {
		if (this._innerContexts.size <= 0) {
			return false;
		}

		await Promise.all(Array.from(this._innerContexts, context =>
			context.handleSubmit(() => MvvmContextState._resolved)
		));

		for (const context of this._innerContexts) {
			if (context.isInvalid()) return true;
		}
		return false;
	}

	// Surface the children's problems through this context's collectors by reference
	// (read-time recursion) instead of copying entries — no stale copies to clean up.
	private _surfaceChildProblems(): void {
		let childErrors = false;
		let childExceptions = false;
		for (const context of this._innerContexts) {
			childErrors ||= context.hasErrors();
			childExceptions ||= context.hasExceptions();
		}
		this._setChildProblemVisibility(childErrors, childExceptions);
	}
}
